import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, ChevronDown, Loader2 } from "lucide-react";
import { http, DOMAIN_SHORT } from "@/lib/api";
import { GradeChart } from "@/components/GradeChart";
import { WeeklyChart, weekly } from "@/components/WeeklyChart";
import { analyse, StrengthsPanel } from "@/components/PerformanceParts";
import { SafetyCard, MissedBasics, MockHistory, Readiness } from "@/components/PerformanceExtras";
import { useAuth } from "@/context/Auth";

const toPts = (vs) => vs.map((v) => ({ score: v.overall_score, at: v.created_at, title: v.case_title }));
const Stat = ({ label, value, testid }) => (
  <div className="card p-5"><p className="text-xs uppercase tracking-widest font-mono" style={{ color: "var(--ink-3)" }}>{label}</p><p data-testid={testid} className="mt-1 font-display text-4xl font-bold">{value}</p></div>
);

const ByDomain = ({ vivas }) => (
  <div data-testid="perf-domain-charts" className="mt-4 grid md:grid-cols-2 gap-4">
    {DOMAIN_SHORT.map((name, i) => {
      const vs = vivas.filter((v) => v.domain === i + 1);
      return (
        <div key={i} className="card p-4">
          <p className="text-sm font-semibold">D{i + 1} {name} <span className="font-mono text-xs font-normal" style={{ color: "var(--ink-3)" }}>×{vs.length}</span></p>
          {vs.length ? <GradeChart data={toPts(vs)} height={170} testid={`perf-domain-chart-${i + 1}`} /> : <p className="mt-6 mb-6 text-xs text-center" style={{ color: "var(--ink-3)" }}>No cases yet</p>}
        </div>
      );
    })}
  </div>
);

export default function Performance() {
  const [data, setData] = useState(null);
  const [byDomain, setByDomain] = useState(false);
  const { user } = useAuth();
  const examDate = user ? user.exam_date : localStorage.getItem("exam-date");
  useEffect(() => { http.get("/stats/performance").then((r) => setData(r.data)).catch(() => setData({ vivas: [], driving: [] })); }, []);
  if (!data) return <div className="py-24 grid place-items-center"><Loader2 className="animate-spin" /></div>;
  const { vivas, driving } = data;
  const a = analyse(vivas, driving);
  const tips = [...new Set(vivas.slice(-5).reverse().flatMap((v) => v.improvements?.slice(0, 1) || []))].slice(0, 5);
  return (
    <div data-testid="performance-page" className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14 pb-24">
      <p className="font-mono text-xs uppercase tracking-widest flex items-center gap-2" style={{ color: "var(--accent)" }}><BarChart3 size={14} /> Performance summary</p>
      <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">How you're tracking</h1>
      <p data-testid="perf-indicative-note" className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>Grades are AI-marked and the 50% pass line is <strong>indicative only</strong>; it is not ANZCA's standard-setting.</p>

      <section className="mt-8 grid lg:grid-cols-[minmax(0,1fr),minmax(0,1.6fr)] gap-4">
        <div className="grid grid-cols-2 lg:grid-cols-1 gap-4">
          <Stat label="Cases completed" value={vivas.length} testid="perf-total-cases" />
          <Stat label="Driving questions" value={driving.length} testid="perf-total-driving" />
        </div>
        <div className="card p-5">
          <h2 className="font-display text-base md:text-lg font-semibold mb-2">Activity per week</h2>
          {vivas.length + driving.length ? <WeeklyChart data={weekly(vivas, driving)} /> : <p className="text-sm py-10 text-center" style={{ color: "var(--ink-3)" }}>Nothing yet.</p>}
        </div>
      </section>

      <section className="mt-10">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div><h2 className="font-display text-base md:text-lg font-semibold">Grades over time</h2><p className="text-xs" style={{ color: "var(--ink-3)" }}>Each point is a case, in order. Green above the pass line, red below. Indicative only.</p></div>
          {vivas.length > 0 && <button data-testid="perf-toggle-domains" onClick={() => setByDomain((b) => !b)} className="btn-ghost h-10 px-4 rounded-full text-sm font-medium flex items-center gap-2">
            {byDomain ? "Hide" : "Show"} each domain <ChevronDown size={15} className={`transition-transform ${byDomain ? "rotate-180" : ""}`} /></button>}
        </div>
        {vivas.length ? (
          <div className="card p-4 mt-3"><GradeChart data={toPts(vivas)} testid="perf-grade-chart" /></div>
        ) : (
          <div data-testid="perf-empty" className="card p-8 mt-3 text-center text-sm" style={{ color: "var(--ink-2)" }}>Complete your first case to start tracking. <Link to="/" className="underline font-medium">Start practising</Link></div>
        )}
        {byDomain && <ByDomain vivas={vivas} />}
      </section>

      <section className="mt-10 grid lg:grid-cols-2 gap-4">
        <Readiness examDate={examDate} domains={a.domains} />
        <SafetyCard vivas={vivas} />
        <MissedBasics vivas={vivas} driving={driving} />
        <MockHistory mocks={data.mocks || []} />
      </section>

      {(vivas.length > 0 || driving.length > 0) && (
        <section className="mt-10">
          <h2 className="font-display text-base md:text-lg font-semibold mb-3">Strengths and weaknesses</h2>
          <StrengthsPanel a={a} recentTips={tips} vivas={vivas} />
        </section>
      )}
    </div>
  );
}
